import { XMLParser as _XMLParser } from "fast-xml-parser";
const XMLParser = _XMLParser;

/* Sections of <head> that never contain document metadata */
const skipSections = ["styling", "layout"];
function asArray(value) {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}
function getText(node) {
  if (node === undefined || node === null) {
    return "";
  }
  if (typeof node !== "object") {
    return String(node).trim();
  }
  return node["#text"] !== undefined ? String(node["#text"]).trim() : "";
}

/* Depth-first search for every node with the given (namespace-less) tag name */
function findAll(node, tagName, found = []) {
  if (!node || typeof node !== "object") {
    return found;
  }
  asArray(node).forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }
    Object.keys(item).forEach(key => {
      if (key.startsWith("@_") || key === "#text" || skipSections.includes(key)) {
        return;
      }
      if (key === tagName) {
        found.push(...asArray(item[key]));
      }
      findAll(item[key], tagName, found);
    });
  });
  return found;
}
function firstText(head, tagName) {
  const match = findAll(head, tagName).map(getText).find(text => text !== "");
  return match || "";
}
function firstAttr(head, tagName, attrName) {
  const node = findAll(head, tagName).find(n => n && typeof n === "object" && n["@_" + attrName]);
  return node ? String(node["@_" + attrName]).trim() : "";
}

/* Extract document level metadata from a TTML/IMSC/SMPTE-TT/EBU-TT/DFXP file.
   Only the root <tt> tag and the <head> are parsed. Returns an object of { rawName: value } with empty values removed.
   Never throws - a malformed header simply returns {}. */
export default (function extractXmlMetadata(input) {
  const result = {};
  try {
    if (typeof input !== "string" || input.length === 0) {
      return result;
    }
    const rootMatch = input.match(/<((?:[\w-]+:)?tt)\b[^>]*>/);
    if (!rootMatch) {
      return result;
    }
    const headMatch = input.match(/<((?:[\w-]+:)?head)\b[^>]*>[\s\S]*?<\/\1>/);
    const xml = `${rootMatch[0]}${headMatch ? headMatch[0] : ""}</${rootMatch[1]}>`;
    const parser = new XMLParser({
      ignoreAttributes: false,
      removeNSPrefix: true,
      parseTagValue: false,
      parseAttributeValue: false,
      trimValues: true
    });
    const json = parser.parse(xml);
    const tt = json.tt || {};
    const head = tt.head || {};
    result["xml:lang"] = tt["@_lang"] ? String(tt["@_lang"]).trim() : "";
    result["ttm:title"] = firstText(head, "title");
    result["ttm:desc"] = firstText(head, "desc");
    result["ttm:copyright"] = firstText(head, "copyright");
    result["m608:programName"] = firstAttr(head, "information", "programName");

    /* ttm:agent can describe people and characters - only a person is treated as the author */
    const author = findAll(head, "agent").filter(agent => agent && typeof agent === "object" && (!agent["@_type"] || agent["@_type"] === "person")).map(agent => firstText(agent, "name")).find(name => name !== "");
    result["ttm:agent"] = author || "";

    /* EBU-TT document metadata */
    result["documentOriginalProgrammeTitle"] = firstText(head, "documentOriginalProgrammeTitle");
    result["documentOriginalEpisodeTitle"] = firstText(head, "documentOriginalEpisodeTitle");
    result["documentPublisher"] = firstText(head, "documentPublisher");
  } catch (err) {
    return {};
  }
  Object.keys(result).forEach(key => {
    if (!result[key]) {
      delete result[key];
    }
  });
  return result;
});